import {
  Autocomplete,
  type AutocompleteChangeDetails,
  type AutocompleteChangeReason,
  type AutocompleteInputChangeReason,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

import {
  ErrorModal,
  MesfModal,
  dxtToLocalServerTime,
  getMomentTz,
} from "@dexteel/mesf-core";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../controls/buttons/FooterModalButtons";
import { useEAFProcessesContext } from "../context/EAFProcessesContext";
import { UnLinkedHeat } from "../models/UnLinkedHeat";
import {
  linkEAFProcess,
  searchUnlinkedHeats,
} from "../repositories/EAFRepository";
import { get } from "lodash-es";

const moment = getMomentTz();

type SelectHeat = {
  heatId: UnLinkedHeat | null;
};

type Props = {
  show: boolean;
  onHide: (shouldUpdate: boolean) => void;
  refreshData: Function;
};
export const LinkEAFProcess = ({ show, onHide, refreshData }: Props) => {
  const { state, actions } = useEAFProcessesContext();
  const process = state.selectedProcess;

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [unlinkedHeats, setUnLinkedHeats] = useState<UnLinkedHeat[]>([]);
  const [selectedHeat, setSelectedHeat] = useState<UnLinkedHeat | null>(null);
  const [searchText, setSearchText] = useState("");

  const [error, setError] = useState("");
  const [showWarningModal, setShowWarningModal] = useState(false);

  const title = `Link EAF Process / Start: ${dxtToLocalServerTime(
    `${moment.utc(process?.Start)}`,
    "HH:mm",
  )}`;

  const { handleSubmit, control, setValue, reset } = useForm<SelectHeat>({});

  const confirmLinkProcess = async (): Promise<void> => {
    if (!process || !selectedHeat) return;
    setIsSubmitLoading(true);
    const response = await linkEAFProcess(
      process.ProcessId,
      selectedHeat.HeatId,
      true,
    );
    setIsSubmitLoading(false);
    if (response.ok) {
      actions.setNotificationMessage("Process linked successfully");
      await refreshData();
      onHide(true);
    } else {
      setError(response.message);
    }
  };

  const onSubmit = async (): Promise<void> => {
    if (!process) {
      setError("Process not found");
      return;
    }

    if (!selectedHeat) {
      setError("Please select a heat");
      return;
    }

    if (selectedHeat.IsLinked) {
      setShowWarningModal(true);
      return;
    }
    await confirmLinkProcess();
  };

  const handleSearchChange = async (): Promise<void> => {
    if (!process) return;
    const heats = await searchUnlinkedHeats(searchText, process.Start as Date);
    if (heats.ok) {
      setUnLinkedHeats(get(heats, "data.tables[0].rows", []));
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ): void => {
    setSearchText(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchChange();
    }
  };

  const handleChange = (
    event: React.ChangeEvent<{}>,
    newValue: UnLinkedHeat | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<UnLinkedHeat> | undefined,
  ): void => {
    setSelectedHeat(newValue);
    setValue("heatId", newValue, { shouldValidate: true });
  };

  useEffect(() => {
    if (show && process) {
      setIsSubmitLoading(false);
      (async () => {
        setSelectedHeat(null);
        setIsLoadingData(true);
        const heats = await searchUnlinkedHeats("", process.Start as Date);
        if (heats.ok) {
          setUnLinkedHeats(get(heats, "data.tables[0].rows", []));
        }
        setIsLoadingData(false);
      })();
    } else {
      reset();
    }
  }, [show, process]);

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            maxWidth="sm"
            open={show}
            handleClose={() => {
              onHide(true);
            }}
            id="linkEAFProcess"
            title={title}
          >
            {isLoadingData ? (
              <>
                <Grid
                  container
                  style={{
                    padding: "60px 280px",
                  }}
                  justifyContent="center"
                  alignContent="center"
                >
                  <Grid item>
                    <CircularProgress />
                  </Grid>
                </Grid>
              </>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <MesfModal.Content
                  style={{
                    padding: "15px 30px",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item md={2}>
                      <Typography
                        style={{
                          margin: 0,
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          height: "56px",
                        }}
                        variant="h6"
                      >
                        To Heat
                      </Typography>
                    </Grid>
                    <Box style={{ height: 70 }}>
                      <Grid item md={10}>
                        <Controller
                          name="heatId"
                          control={control}
                          rules={{ required: "Please select a heat" }}
                          render={({ field, fieldState: { error } }) => (
                            <Autocomplete
                              {...field}
                              options={unlinkedHeats}
                              getOptionLabel={(option) => option.HeatName}
                              value={selectedHeat}
                              onChange={handleChange}
                              inputValue={searchText}
                              onInputChange={handleInputChange}
                              renderOption={(props, option) => (
                                <li
                                  {...props}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <span>{option.HeatName}</span>
                                  {option.IsLinked && (
                                    <span
                                      style={{
                                        color: "red",
                                        marginLeft: "10px",
                                      }}
                                    >
                                      (linked)
                                    </span>
                                  )}
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Search Heat"
                                  variant="outlined"
                                  fullWidth
                                  onKeyDown={handleKeyDown}
                                  error={!!error?.message}
                                  helperText={error ? error.message : null}
                                  slotProps={{
                                    input: {
                                      ...params.InputProps,
                                      style: {
                                        height: "100%",
                                        minWidth: "400px",
                                      },
                                    },
                                  }}
                                />
                              )}
                            />
                          )}
                        />
                      </Grid>
                    </Box>
                  </Grid>
                </MesfModal.Content>
                <MesfModal.Actions style={{ padding: "20px " }}>
                  <FooterModalButtons
                    isSubmitLoading={isSubmitLoading}
                    onHide={onHide}
                  />
                </MesfModal.Actions>
              </form>
            )}
          </MesfModal>
        </Grid>
      </Grid>

      <MesfModal
        open={showWarningModal}
        handleClose={() => setShowWarningModal(false)}
        title="Warning"
        id="warningModal"
      >
        <MesfModal.Content>
          <p>
            This heat is already linked in another process. Do you want to
            proceed?
          </p>
        </MesfModal.Content>
        <MesfModal.Actions>
          <Button
            fullWidth
            variant="contained"
            color="inherit"
            onClick={() => setShowWarningModal(false)}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={async () => {
              await confirmLinkProcess();
              setShowWarningModal(false);
            }}
          >
            Proceed
          </Button>
        </MesfModal.Actions>
      </MesfModal>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
